import { Navigate } from "react-router-dom";
import * as Sentry from "@sentry/react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  ensureSentryClientForSmokeTest,
  getSentryDsnHostHint,
  hasEmbeddedSentryDsn,
} from "@/lib/error-tracking";

function isSentryTestPageEnabled() {
  // Local dev: always allow this obscure URL so you can verify routing without env churn.
  if (import.meta.env.DEV) return true;

  // Production / preview: must opt in. Vite only embeds VITE_* vars at build time — redeploy after changing them.
  const raw = String(import.meta.env.VITE_ENABLE_SENTRY_TEST ?? "").trim().toLowerCase();
  return raw === "true" || raw === "1" || raw === "yes";
}

/**
 * Hidden Sentry smoke test. Open: /__debug/sentry-test
 * Requires VITE_SENTRY_DSN (dev: .env.local; deploy: host env before build).
 * On deployed sites, set VITE_ENABLE_SENTRY_TEST=true (or 1 / yes) and rebuild.
 */
export default function SentryTestPage() {
  if (!isSentryTestPageEnabled()) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="flex min-h-[50vh] flex-col items-center justify-center gap-6 p-8">
      <p className="max-w-md text-center text-sm text-muted-foreground">
        Sentry runs whenever <code className="rounded bg-muted px-1">VITE_SENTRY_DSN</code> is set (e.g.{" "}
        <code className="rounded bg-muted px-1">.env.local</code> for dev, or your host env before{" "}
        <code className="rounded bg-muted px-1">vite build</code>). Remove{" "}
        <code className="rounded bg-muted px-1">VITE_ENABLE_SENTRY_TEST</code> on public deploys when done.
      </p>
      {!hasEmbeddedSentryDsn() && (
        <p className="max-w-lg rounded-md border border-destructive/50 bg-destructive/10 px-4 py-3 text-center text-sm text-destructive">
          <strong>No DSN in this bundle.</strong> Set <code className="rounded bg-muted px-1">VITE_SENTRY_DSN</code>{" "}
          and restart dev or redeploy.
        </p>
      )}
      {hasEmbeddedSentryDsn() && (
        <p className="max-w-lg text-center text-xs text-muted-foreground">
          Ingest host in this bundle: <code className="rounded bg-muted px-1">{getSentryDsnHostHint() ?? "?"}</code>
          . If events never show: open DevTools → <strong>Network</strong>, click the button, look for requests to{" "}
          <code className="rounded bg-muted px-1">ingest</code> — ad blockers often block them. Set{" "}
          <code className="rounded bg-muted px-1">VITE_SENTRY_DEBUG=true</code> and reload for SDK logs.
        </p>
      )}
      <Button
        variant="destructive"
        onClick={async () => {
          ensureSentryClientForSmokeTest();

          if (!Sentry.getClient()) {
            toast.error("Sentry not initialized. Set VITE_SENTRY_DSN and restart dev or rebuild.");
            return;
          }

          const err = new Error("This is your first error!");
          const eventId = Sentry.captureException(err);
          Sentry.captureMessage("Sentry smoke test (captureMessage)", "error");

          try {
            await Sentry.flush(5000);
          } catch {
            /* ignore */
          }

          if (eventId) {
            toast.success(`Event queued (id ${String(eventId).slice(0, 8)}…). Check Sentry → Issues for this project.`);
          } else {
            toast.warning(
              "No event id returned — SDK may have dropped the event. Try disabling ad blockers or set VITE_SENTRY_DEBUG=true.",
            );
          }
        }}
      >
        Break the world
      </Button>
    </div>
  );
}
